import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  message,
  Descriptions,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Popconfirm,
  TimePicker
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * 加班申请管理页面 - 供管理员审批使用
 */
const OvertimeManagement = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [approvalVisible, setApprovalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: null
  });
  const [form] = Form.useForm();
  const { user, token, isAuthenticated, isInitialized } = useAuthStore();
  // 补登相关状态
  const [supplementVisible, setSupplementVisible] = useState(false);
  const [supplementForm] = Form.useForm();
  const [userOptions, setUserOptions] = useState([]);

  /**
   * 获取加班申请列表（需登录后携带Token）
   * 说明：
   * 1）后端返回结构为 { success, data: { applications: [...], pagination } }
   * 2）支持通过查询参数 status 过滤：pending/approved/rejected
   * 3）将后端状态值归一化为小写的 pending/approved/rejected，确保前端过滤命中
   * 4）如仅返回 overtimeSlots，则从第一个时间段回填日期/时间
   */
  const fetchApplications = useCallback(async () => {
    if (!token) return; // 无token不请求
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      const query = params.toString();
      const url = query ? `${apiUrl}/overtime?${query}` : `${apiUrl}/overtime`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // 兼容后端两种返回结构：data.data.applications 或 data.applications
        const rawApps = data?.data?.applications || data?.applications || data?.data || [];
        // 将后端记录映射为前端表格所需的扁平结构
        const mapped = Array.isArray(rawApps) ? rawApps.map(app => {
          // 状态归一化
          const rawStatus = app.status || app.state || app.approvalStatus || app.approval_status;
          let normStatus = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : rawStatus;
          if (typeof normStatus === 'number') {
            normStatus = normStatus === 0 ? 'pending' : normStatus === 1 ? 'approved' : normStatus === 2 ? 'rejected' : 'unknown';
          } else if (typeof normStatus === 'string') {
            if (['awaiting', 'waiting', 'in_review', 'to_approve'].includes(normStatus)) normStatus = 'pending';
            if (['pass', 'accepted'].includes(normStatus)) normStatus = 'approved';
            if (['deny', 'denied'].includes(normStatus)) normStatus = 'rejected';
          }

          // 时间段回填：优先使用扁平字段，其次使用 overtimeSlots 的第一个时间段
          const slots = app.overtimeSlots || app.overtime_slots;
          const firstSlot = Array.isArray(slots) && slots.length > 0 ? slots[0] : null;
          const overtimeDate = app.overtimeDate || app.overtime_date || firstSlot?.date || firstSlot?.overtimeDate || firstSlot?.overtime_date;
          const startTime = app.startTime || app.start_time || firstSlot?.startTime || firstSlot?.start_time;
          const endTime = app.endTime || app.end_time || firstSlot?.endTime || firstSlot?.end_time;

          return ({
            id: app.id,
            // 申请人名称优先使用真实姓名，其次用户名
            userName: app?.applicant?.realName || app?.applicant?.real_name || app?.applicant?.username || app?.username || app?.userName || '未知',
            overtimeDate,
            startTime,
            endTime,
            duration: app.duration, // 若后端未提供可作为可选字段
            reason: app.reason,
            status: normStatus,
            // 关联信息
            projectName: app?.project?.name || app?.projectName,
            taskTitle: app?.task?.title || app?.taskTitle,
            // 审批信息
            createdAt: app.createdAt || app.created_at,
            approvedBy: app.approvedBy || app.approved_by,
            approverName: app?.approver?.realName || app?.approver?.real_name || app?.approver?.username || app?.approverName,
            approvedAt: app.approvedAt || app.approved_at,
            approvalComment: app.approvalComment || app.approval_comment
          });
        }) : [];
        setApplications(mapped);
      } else {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || '获取加班申请列表失败');
      }
    } catch (error) {
      console.error('获取加班申请列表错误:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [token, filters.status]);

  /**
   * 获取统计信息（需登录后携带Token）
   * 说明：后端返回结构为 { success, data: {...} }，需读取 data.data
   */
  const fetchStats = useCallback(async () => {
    if (!token) return; // 无token不请求
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/overtime/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data?.data || data || {});
      }
    } catch (error) {
      console.error('获取统计信息错误:', error);
    }
  }, [token]);

  /**
   * 审批加班申请（需登录后携带Token）
   */
  const handleApproval = async (values) => {
    if (!token || !selectedApplication?.id) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/overtime/${selectedApplication.id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: values.status,
          approvalComment: values.approvalComment
        })
      });

      if (response.ok) {
        message.success('审批完成');
        setApprovalVisible(false);
        form.resetFields();
        fetchApplications();
        fetchStats();
      } else {
        const errorData = await response.json();
        message.error(errorData.message || '审批失败');
      }
    } catch (error) {
      console.error('审批错误:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  /**
   * 取消（删除）加班申请（待审批或已通过）
   * - 待审批：申请人或管理员可取消
   * - 已通过：仅管理员或主管可取消
   */
  const handleCancel = async (id) => {
    if (!token || !id) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/overtime/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        message.success('已取消该申请');
        fetchApplications();
        fetchStats();
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.message || '取消失败');
      }
    } catch (error) {
      console.error('取消申请错误:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 获取用户列表（用于补登选择员工）
  const fetchUsers = async () => {
    if (!token) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const res = await fetch(`${apiUrl}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const list = data?.data?.users || data?.users || data || [];
      const options = Array.isArray(list) ? list.map(u => ({
        value: u.id,
        label: u.realName || u.real_name || u.username
      })) : [];
      setUserOptions(options);
    } catch (e) {
      console.error('获取用户列表失败:', e);
    }
  };

  // 打开补登弹窗
  const openSupplement = () => {
    setSupplementVisible(true);
    supplementForm.resetFields();
    supplementForm.setFieldsValue({ reason: '补登' });
    fetchUsers();
  };

  // 新增：快捷时间设置
  const setFullDay = () => {
    supplementForm.setFieldsValue({
      startTime: dayjs('08:30', 'HH:mm'),
      endTime: dayjs('17:30', 'HH:mm')
    });
  };

  const setHalfDay = () => {
    supplementForm.setFieldsValue({
      startTime: dayjs('13:30', 'HH:mm'),
      endTime: dayjs('17:30', 'HH:mm')
    });
  };

  // 提交补登
  const handleSupplementSubmit = async () => {
    try {
      const values = await supplementForm.validateFields();
      const date = values.date.format('YYYY-MM-DD');
      const startTime = values.startTime.format('HH:mm');
      const endTime = values.endTime.format('HH:mm');

      const toMin = (t) => {
        const [h, m] = String(t).split(':').map(Number);
        return h * 60 + m;
      };
      if (toMin(endTime) <= toMin(startTime)) {
        message.error('结束时间必须晚于开始时间');
        return;
      }

      const payload = {
        isSupplement: true,
        autoApprove: true,
        userId: values.userId,
        overtimeSlots: [{
          date,
          startTime,
          endTime,
          reason: values.reason || '补登'
        }]
      };

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const res = await fetch(`${apiUrl}/overtime`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const resp = await res.json().catch(() => ({}));
      if (res.ok) {
        // 如果后端未自动审批，则尝试主动审批为通过
        const list = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
        const firstId = Array.isArray(list) ? list[0]?.id : (resp?.data?.id || resp?.id);
        if (firstId) {
          try {
            await fetch(`${apiUrl}/overtime/${firstId}/approve`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ status: 'approved', approvalComment: '补登自动通过' })
            });
          } catch (e) {
            // 如果已是通过或接口不允许重复审批，忽略错误
            console.warn('补登自动审批失败或已通过', e);
          }
        }

        message.success('补登成功，已自动通过');
        setSupplementVisible(false);
        supplementForm.resetFields();
        fetchApplications();
        fetchStats();
      } else {
        message.error(resp?.message || '补登失败');
      }
    } catch (e) {
      if (e?.errorFields) return; // 表单校验错误
      console.error('补登提交错误:', e);
      message.error('网络错误，请稍后重试');
    }
  };

   /**
    * 显示申请详情
    */
   const showDetail = (record) => {
    setSelectedApplication(record);
    setDetailVisible(true);
  };

  /**
   * 显示审批对话框
   */
  const showApproval = (record) => {
     setSelectedApplication(record);
     setApprovalVisible(true);
     form.setFieldsValue({ approvalComment: '暂无意见' });
   };

  /**
   * 获取状态标签
   */
  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待审批' },
      approved: { color: 'green', text: '已通过' },
      rejected: { color: 'red', text: '已拒绝' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  /**
   * 过滤申请列表
   */
  const getFilteredApplications = () => {
    let filtered = applications;

    // 状态过滤
    if (filters.status !== 'all') {
      filtered = filtered.filter(app => app.status === filters.status);
    }

    // 日期范围过滤
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [start, end] = filters.dateRange;
      filtered = filtered.filter(app => {
        const appDate = dayjs(app.overtimeDate);
        return appDate.isAfter(start.startOf('day')) && appDate.isBefore(end.endOf('day'));
      });
    }

    return filtered;
  };

  // 表格列定义
  const formatTimeToMinutes = (t) => {
    if (!t) return '';
    if (dayjs.isDayjs(t)) return t.format('HH:mm');
    if (typeof t === 'string') {
      const s = t.trim();
      if (s.includes('T') || s.includes(' ')) {
        const parsed = dayjs(s);
        return parsed.isValid() ? parsed.format('HH:mm') : (s.length >= 5 ? s.slice(0,5) : s);
      }
      return s.length >= 5 ? s.slice(0,5) : s;
    }
    const parsed = dayjs(t);
    return parsed.isValid() ? parsed.format('HH:mm') : '';
  };
  const columns = [
    {
      title: '申请人',
      dataIndex: 'userName',
      key: 'userName',
      render: (text) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      )
    },
    {
      title: '加班日期',
      dataIndex: 'overtimeDate',
      key: 'overtimeDate',
      render: (date) => (
        <Space>
          <CalendarOutlined />
          {dayjs(date).format('YYYY-MM-DD')}
        </Space>
      )
    },
    {
       title: '时间段',
       key: 'timeRange',
       render: (_, record) => (
         <Space>
           <ClockCircleOutlined />
           {formatTimeToMinutes(record.startTime)} - {formatTimeToMinutes(record.endTime)}
         </Space>
       )
     },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => `${duration}小时`
    },
    {
      title: '项目',
      dataIndex: 'projectName',
      key: 'projectName'
    },
    {
      title: '任务',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showDetail(record)}
          >
            详情
          </Button>
          {record.status === 'pending' && (
            <Button
              type="link"
              icon={<CheckOutlined />}
              onClick={() => showApproval(record)}
            >
              审批
            </Button>
          )}
          {record.status === 'approved' && (
            <Popconfirm
              title="确认取消此申请？"
              description="取消后不可恢复"
              onConfirm={() => handleCancel(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button type="link" danger icon={<CloseOutlined />}>取消</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  /**
   * 页面初始化后拉取数据：等待认证初始化完成，并且已登录且有token
   * 当状态筛选变化时自动重新请求
   */
  useEffect(() => {
    if (!isInitialized) return; // 等待初始化完成
    if (!isAuthenticated || !token) return; // 未登录不请求
    fetchApplications();
    fetchStats();
  }, [isInitialized, isAuthenticated, token, fetchApplications]);

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总申请数"
              value={stats.totalApplications || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待审批"
              value={stats.pendingApplications || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已通过"
              value={stats.approvedApplications || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总加班时长"
              value={stats.totalHours || 0}
              suffix="小时"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 过滤器 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <span>状态：</span>
            <Select
              value={filters.status}
              style={{ width: 120 }}
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Option value="all">全部</Option>
              <Option value="pending">待审批</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Col>
          <Col>
            <span>日期范围：</span>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              placeholder={['开始日期', '结束日期']}
            />
          </Col>
          <Col>
            <Button
              onClick={() => setFilters({ status: 'all', dateRange: null })}
            >
              重置
            </Button>
          </Col>
          <Col>
            <Button type="primary" onClick={openSupplement}>
              补登
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 申请列表 */}
      <Card title="加班申请列表">
        <Table
          columns={columns}
          dataSource={getFilteredApplications()}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
        />
      </Card>

      {/* 补登对话框 */}
      <Modal
        title="补登加班"
        open={supplementVisible}
        onCancel={() => { setSupplementVisible(false); supplementForm.resetFields(); }}
        footer={null}
        width={520}
      >
        <Form
          form={supplementForm}
          layout="vertical"
          onFinish={handleSupplementSubmit}
          initialValues={{ reason: '补登' }}
        >
          <Form.Item name="userId" label="员工" rules={[{ required: true, message: '请选择员工' }]}>
            <Select
              placeholder="选择员工"
              options={userOptions}
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item name="date" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="startTime" label="开始时间" rules={[{ required: true, message: '请选择开始时间' }]}>
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endTime" label="结束时间" rules={[{ required: true, message: '请选择结束时间' }]}>
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button onClick={setFullDay}>全天</Button>
              <Button onClick={setHalfDay}>半天</Button>
            </Space>
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <TextArea rows={3} placeholder="默认填入补登，可自定义" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setSupplementVisible(false); supplementForm.resetFields(); }}>取消</Button>
              <Button type="primary" htmlType="submit">提交补登</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情对话框 */}
      <Modal
        title="加班申请详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>,
          selectedApplication?.status === 'pending' && (
            <Button
              key="approve"
              type="primary"
              onClick={() => {
                setDetailVisible(false);
                showApproval(selectedApplication);
              }}
            >
              审批
            </Button>
          )
        ].filter(Boolean)}
        width={600}
      >
        {selectedApplication && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="申请人" span={2}>
              {selectedApplication.userName}
            </Descriptions.Item>
            <Descriptions.Item label="加班日期">
              {dayjs(selectedApplication.overtimeDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="时间段">
              {selectedApplication.startTime} - {selectedApplication.endTime}
            </Descriptions.Item>
            <Descriptions.Item label="加班时长">
              {selectedApplication.duration}小时
            </Descriptions.Item>
            <Descriptions.Item label="项目">
              {selectedApplication.projectName}
            </Descriptions.Item>
            <Descriptions.Item label="任务" span={2}>
              {selectedApplication.taskTitle}
            </Descriptions.Item>
            <Descriptions.Item label="加班原因" span={2}>
              {selectedApplication.reason}
            </Descriptions.Item>
            <Descriptions.Item label="申请状态">
              {getStatusTag(selectedApplication.status)}
            </Descriptions.Item>
            <Descriptions.Item label="申请时间">
              {dayjs(selectedApplication.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            {selectedApplication.approvedBy && (
              <>
                <Descriptions.Item label="审批人">
                  {selectedApplication.approverName}
                </Descriptions.Item>
                <Descriptions.Item label="审批时间">
                  {dayjs(selectedApplication.approvedAt).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                {selectedApplication.approvalComment && (
                  <Descriptions.Item label="审批意见" span={2}>
                    {selectedApplication.approvalComment}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* 审批对话框 */}
      <Modal
        title="审批加班申请"
        open={approvalVisible}
        onCancel={() => {
          setApprovalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        {selectedApplication && (
          <>
            <Descriptions column={1} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="申请人">
                {selectedApplication.userName}
              </Descriptions.Item>
              <Descriptions.Item label="加班时间">
                {dayjs(selectedApplication.overtimeDate).format('YYYY-MM-DD')} {selectedApplication.startTime} - {selectedApplication.endTime}
              </Descriptions.Item>
              <Descriptions.Item label="时长">
                {selectedApplication.duration}小时
              </Descriptions.Item>
              <Descriptions.Item label="原因">
                {selectedApplication.reason}
              </Descriptions.Item>
            </Descriptions>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleApproval}
              initialValues={{ approvalComment: '暂无意见' }}
            >
              <Form.Item
                name="status"
                label="审批结果"
                rules={[{ required: true, message: '请选择审批结果' }]}
              >
                <Select placeholder="请选择审批结果">
                  <Option value="approved">通过</Option>
                  <Option value="rejected">拒绝</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="approvalComment"
                label="审批意见"
                rules={[{ required: true, message: '请填写审批意见' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="请填写审批意见..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setApprovalVisible(false);
                    form.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    提交审批
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default OvertimeManagement;
